import { twMerge } from 'tailwind-merge';
import React, { useEffect, useRef, useState } from 'react';
import RemoveAttachmentButton from './RemoveAttachmentButton';

interface Props {
    imageSrc: string;
    fileName: string;
    handleRemoveAttachment: () => void;
    file?: File;
    onAspectRatioFound?: (aspectRatio: number) => void;
}

const AttachmentPreview = ({
    imageSrc,
    fileName,
    handleRemoveAttachment,
    file,
    onAspectRatioFound,
}: Props) => {
    const imageRef = useRef<HTMLImageElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [imgWidth, setImgWidth] = useState(0);
    const [videoObjectUrl, setVideoObjectUrl] = useState<string | null>(
        file?.type?.startsWith('video') ? URL.createObjectURL(file) : null,
    );

    useEffect(() => {
        if (file?.type?.startsWith('video')) {
            setVideoObjectUrl(URL.createObjectURL(file));
        } else {
            setVideoObjectUrl(null);
        }
    }, [file]);

    useEffect(() => {
        if (imageSrc) {
            setImgWidth(imageRef.current?.offsetWidth);
        }
    }, [imageSrc]);

    useEffect(() => {
        return () => {
            if (videoObjectUrl) {
                URL.revokeObjectURL(videoObjectUrl);
            }
        };
    }, [videoObjectUrl]);

    const getVideoOrImagePreview = () => {
        if (videoObjectUrl) {
            return (
                <video
                    className="w-fit rounded-sm"
                    controls={true}
                    preload="metadata"
                    src={videoObjectUrl}
                    ref={videoRef}
                    onLoadedMetadata={() => {
                        onAspectRatioFound?.(
                            videoRef.current?.videoWidth /
                                videoRef.current?.videoHeight,
                        );
                    }}
                />
            );
        }

        if (imageSrc) {
            return (
                <div
                    className={twMerge(
                        'flex items-center justify-center',
                        imgWidth < 180 && 'bg-gray-150',
                    )}
                >
                    <img
                        ref={imageRef}
                        src={imageSrc}
                        alt={fileName}
                        className={twMerge(
                            'object-cover',
                            imgWidth >= 180 && 'rounded-t-sm',
                        )}
                        onLoad={() => {
                            onAspectRatioFound?.(
                                imageRef.current?.offsetWidth /
                                    imageRef.current?.offsetHeight,
                            );
                        }}
                    />
                </div>
            );
        }

        return null;
    };

    return (
        <div className="flex bg-gray-100 first:rounded-s-sm last:rounded-b-sm">
            <div className="ms-3 mt-2 mb-3 flex w-[180px] flex-col rounded-sm bg-gray-200">
                {getVideoOrImagePreview()}
                <div className="flex items-center justify-between rounded-b-sm p-2">
                    <p className="overflow-hidden text-sm font-normal text-ellipsis text-gray-800">
                        {fileName}
                    </p>
                    <div className="hover:darken-10 flex size-8 items-center justify-center rounded-sm text-gray-600 hover:text-gray-700">
                        <RemoveAttachmentButton
                            handleRemoveAttachment={handleRemoveAttachment}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AttachmentPreview;
